import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_HOST } from "../../constants";
import { useSession } from "../Login/SessionContext";

import BbsList from "./BbsList";
import BbsNoticeList from "./BbsNoticeList";
import Pagination from "../ui/Pagination";
import BbsCategoryTabs from "./BbsCategoryTabs";
import LoadingAnime from "../Common/LoadingAnime";
import EmptyState from "../ui/EmptyState";
import Button from "../ui/Button";

function Bbs({ showBbsModule = true, compact = false, activeDocumentId = null }) {
  const { isLoading, user } = useSession();
  const { moduleName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [module, setModule] = useState({});
  const [posts, setPosts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page"), 10) || 1);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_HOST}/api/bbs/${moduleName}/categories/`);
        setCategories(response.data);
      } catch (error) {
        setError(error.response?.data || "Error loading categories");
      }
    };

    const fetchModuleInfo = async () => {
      try {
        const response = await axios.get(`${API_HOST}/api/modules/`, {
          params: { module_name: moduleName },
        });

        if (response.data.length > 0) {
          setModule(response.data[0]);
        } else {
          setError("Board module not found");
        }
      } catch (error) {
        setError("Error fetching board module");
      }
    };

    fetchCategories();
    fetchModuleInfo();
  }, [moduleName]);

  useEffect(() => {
    const fetchBbs = async () => {
      try {
        setIsFetching(true);
        const params = { page: currentPage };
        if (selectedCategory) {
          params.category = selectedCategory;
        }
        const response = await axios.get(`${API_HOST}/api/bbs/${moduleName}/`, { params });
        setNotices(response.data.notices || []);
        setPosts(response.data.results);
        setTotalPages(response.data.total_pages);
      } catch (error) {
        setError("Error fetching posts");
      } finally {
        setIsFetching(false);
      }
    };

    fetchBbs();
  }, [moduleName, currentPage, selectedCategory]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const params = new URLSearchParams(searchParams);
    params.set("page", pageNumber);
    navigate(`?${params.toString()}`);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    const params = new URLSearchParams(searchParams);
    if (category) {
      params.set("category", category);
    } else {
      params.delete("category");
    }
    params.set("page", 1);
    navigate(`?${params.toString()}`);
  };

  if (isLoading) return <LoadingAnime label="게시판을 불러오는 중..." />;

  if (error) {
    return (
      <div className="glass-card p-6">
        <h4 className="text-lg font-semibold text-white">문제가 발생했습니다</h4>
        <p className="mt-2 text-sm text-ink-200">{error}</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-4">
        {posts.length > 0 ? (
          <>
          <BbsList
            posts={posts}
            moduleName={moduleName}
            currentPage={currentPage}
            selectedCategory={selectedCategory}
            activeId={activeDocumentId}
          />
            <section className="glass-card p-4 sm:p-6">
              <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} />
            </section>
          </>
        ) : (
          <EmptyState title="등록된 게시글이 없습니다" description="다른 게시글을 확인해보세요." />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="glass-card p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-white">{module.browser_title || module.name}</h2>
              <p className="text-xs text-ink-200">{module.header_content || "오늘의 썰을 공유하고 대화에 참여하세요."}</p>
            </div>
            {user && <Button onClick={() => navigate(`/bbs/write/${moduleName}`)}>글쓰기</Button>}
          </div>
          <BbsCategoryTabs categories={categories} onSelectCategory={handleCategoryChange} selectedCategory={selectedCategory} />
          {notices.length > 0 && (
            <div className="space-y-2">
              <BbsNoticeList posts={notices} moduleName={moduleName} />
            </div>
          )}
          {isFetching ? (
            <LoadingAnime label="게시판을 불러오는 중..." />
          ) : posts.length > 0 ? (
          <BbsList
            posts={posts}
            moduleName={moduleName}
            currentPage={currentPage}
            selectedCategory={selectedCategory}
            activeId={activeDocumentId}
          />
          ) : (
            <EmptyState
              title="등록된 게시글이 없습니다"
              description="첫 번째 이야기를 남겨보세요."
              action={user ? <Button onClick={() => navigate(`/bbs/write/${moduleName}`)}>첫 글 작성</Button> : null}
            />
          )}
        </div>
      </section>

      <section className="glass-card p-4 sm:p-6">
        <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} />
      </section>

      {showBbsModule && module.footer_content && (
        <section className="glass-card p-4 text-sm text-ink-200 sm:p-6">{module.footer_content}</section>
      )}
    </div>
  );
}

export default Bbs;
